/*
 * Sound (Alik, 22.10.2026).
 *
 * One quiet sound for everything a person presses: a soft wooden tick, the
 * same on a link, a button, the burger, a size, a story. The first version
 * gave every action its own sound (fabric swish for the menu, whoosh for
 * stories, a zipper for the cart…) and it startled people ("надто різке,
 * пугає"), so it is now a single, low, short tick: no sound differs from
 * another, nothing sweeps or rings.
 *
 * The sonic logo "Слеш" stays for two brand moments only: the end of the
 * intro preloader (where the browser already allows sound) and switching
 * sound on with the toggle.
 *
 * Sounds: assets/cp-sounds.mp3 (13 KB): the tick (Kenney "Impact Sounds"
 * impactWood_light_001, low-passed at 5 kHz) and the logo. CC0; sources in
 * docs/sounds-licenses.md, build in clean-project/ops/sound-design.
 *
 * Who hears it: everyone, from the first visit; the "Звук" toggle in the
 * menus and the footer turns it off (localStorage cp:sound).
 *
 * Browser rules this follows: no sound before the first tap or key (the
 * AudioContext resumes inside that gesture); the iOS ringer switch and other
 * apps' music are respected (audioSession "ambient"); nothing plays in a
 * background tab; one tick per tap, at most one per 80 ms. A light buzz goes
 * with the tick on Android (iPhone browsers give sites no vibration).
 *
 * API: window.cpSound.play(name) · .enabled (get / set). Every name other
 * than 'logo' plays the tick, so the old calls in mobile-shop.js,
 * mega-shop.js and cp-stories.js keep working.
 */
(function () {
  var SRC = document.currentScript && document.currentScript.dataset.src;
  var MAP = { tick: [0.0, 0.15], logo: [0.21, 0.79] };
  // Master level. −10 dB was too loud (Alik, 22.10.2026); back to −20.
  var VOLUME_DB = -20;
  var LEVEL = { tick: -2 };
  var store = {
    get: function (k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
    set: function (k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
  };

  function enabled() { return store.get('cp:sound') !== 'off'; }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'cp_sound_state', cp_sound_on: enabled() });

  var ctx = null, gain = null, buffer = null, loading = null, last = {};
  function quiet() { return window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches; }
  function load() {
    if (loading || !SRC || !enabled()) return loading;
    var AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    try { if (navigator.audioSession) navigator.audioSession.type = 'ambient'; } catch (e) {}
    ctx = new AC();
    gain = ctx.createGain();
    gain.gain.value = Math.pow(10, (VOLUME_DB - (quiet() ? 6 : 0)) / 20);
    gain.connect(ctx.destination);
    loading = fetch(SRC).then(function (r) { return r.arrayBuffer(); })
      .then(function (a) { return new Promise(function (ok, no) { ctx.decodeAudioData(a, ok, no); }); })
      .then(function (b) { buffer = b; })
      .catch(function () { loading = null; });
    return loading;
  }

  function play(name) {
    name = name === 'logo' ? 'logo' : 'tick';
    if (!enabled()) return;
    var now = performance.now();
    if (last[name] && now - last[name] < 80) return;
    last[name] = now;
    if (name === 'tick' && navigator.vibrate) { try { navigator.vibrate(6); } catch (e) {} }
    if (document.hidden || !ctx || !buffer) return;
    // Video sound in the stories viewer wins.
    if (document.querySelector('.cp-sv.is-unmuted[open]')) return;
    if (ctx.state === 'suspended') ctx.resume();
    var seg = MAP[name];
    var s = ctx.createBufferSource();
    s.buffer = buffer;
    if (name === 'tick') s.playbackRate.value = 1 + (Math.random() * 0.04 - 0.02);
    if (LEVEL[name]) {
      var g = ctx.createGain();
      g.gain.value = Math.pow(10, LEVEL[name] / 20);
      s.connect(g); g.connect(gain);
    } else s.connect(gain);
    s.start(0, seg[0], seg[1]);
  }

  /* The tick: every tap on something you can press. Taps on plain text or
     images do nothing; the 80 ms guard in play() keeps one tap to one tick
     when a module also calls cpSound.play for the same tap. */
  var PRESSABLE = 'a[href], button, [role="button"], label, summary, select, input[type="checkbox"], input[type="radio"], input[type="submit"], .minimal-card-size, [data-cp-story], .cp-sv__zone';
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest && e.target.closest(PRESSABLE);
    if (!t || t.disabled || t.closest('[data-cp-sound-toggle]')) return;
    play('tick');
  }, true);

  /* The first tap or key unlocks audio (must happen inside the gesture). */
  function unlock() {
    if (ctx && ctx.state === 'suspended') ctx.resume();
    if (!loading) load();
  }
  ['pointerdown', 'keydown', 'touchstart'].forEach(function (t) {
    document.addEventListener(t, unlock, { capture: true, passive: true });
  });
  var idle = window.requestIdleCallback || function (f) { return setTimeout(f, 1500); };
  window.addEventListener('load', function () { idle(function () { load(); }); }, { once: true });

  /* The sonic logo at the end of the intro preloader. Browsers allow it only
     when the person has tapped or pressed a key during the intro; otherwise
     the AudioContext is still suspended and the wipe stays silent. The
     sprite starts loading at once while the intro runs. */
  var intro = document.documentElement.classList.contains('cp-pl') && !document.documentElement.classList.contains('cp-pl-q');
  if (intro) load();
  document.addEventListener('cp:preloader', function (e) {
    var d = e.detail || {};
    if (d.phase !== 'wipe' || d.mode !== 'intro' || !ctx) return;
    var go = function () { if (ctx.state === 'running') play('logo'); };
    if (buffer) go(); else if (loading) loading.then(go);
  });

  /* ── Toggle: any [data-cp-sound-toggle] ── */
  function paint() {
    var on = enabled();
    document.querySelectorAll('[data-cp-sound-toggle]').forEach(function (b) {
      b.setAttribute('aria-pressed', String(on));
      var l = b.querySelector('[data-cp-sound-label]');
      if (l) l.textContent = on ? 'Звук увімкнено' : 'Звук вимкнено';
      b.classList.toggle('is-on', on);
    });
  }
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest && e.target.closest('[data-cp-sound-toggle]');
    if (!b) return;
    e.preventDefault();
    var on = !enabled();
    store.set('cp:sound', on ? 'on' : 'off');
    window.dataLayer.push({ event: 'cp_sound_toggle', cp_sound_on: on });
    paint();
    if (on) { (load() || Promise.resolve()).then(function () { play('logo'); }); }
  });
  document.addEventListener('DOMContentLoaded', paint);
  new MutationObserver(function () { if (document.querySelector('[data-cp-sound-toggle]:not([aria-pressed])')) paint(); })
    .observe(document.documentElement, { childList: true, subtree: true });

  window.cpSound = {
    play: play,
    get enabled() { return enabled(); },
    set enabled(v) { store.set('cp:sound', v ? 'on' : 'off'); paint(); if (v) load(); }
  };
  paint();
})();
